import Link from "next/link";
import React, { ChangeEvent, useState } from "react";
import {
  Alert,
  Badge,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import Button from "~/components/Button";
import FormModal from "~/components/FormModal";
import GlobalAuthHeader from "~/components/GlobalAuthHeader";
import IconButton from "~/components/IconButton";
import { Restricted } from "~/components/Restricted";
import useApi, { post, del } from "~/hooks/use-api";
import type { Domain, Provider } from "~/gen/rpc/sso";
import {
  initialProvider,
  newProviderSchema,
  ProviderFormFields,
} from "./providers/[providerId]";

const NEW_PROVIDER_OPTION = "__new__";

export default function SsoPage(): JSX.Element {
  return (
    <>
      <GlobalAuthHeader active="sso" />
      <Container>
        <Row className="align-items-center">
          <Col>
            <h5>SSO Domains</h5>
          </Col>
          <Restricted require="privileged">
            <Col sm="auto">
              <Link href="/global-auth/sso/providers" passHref>
                <a>Manage providers →</a>
              </Link>
            </Col>
          </Restricted>
        </Row>
        <p>
          Configure an email domain to an SSO provider. Users with email
          addresses on that domain will be sent through SSO.
        </p>
        <Domains />
      </Container>
    </>
  );
}

function Domains() {
  const { data: domains, revalidate: revalidateDomains } = useApi<Domain[]>(
    "/api/global-auth/sso-domains"
  );
  const { data: providers, revalidate: revalidateProviders } = useApi<
    Provider[]
  >("/api/global-auth/sso-providers");
  const { data: availableProviders, revalidate: revalidateAvailable } = useApi<
    Provider[]
  >("/api/global-auth/sso-providers/available");

  const [domain, setDomain] = useState("");
  const [providerId, setProviderId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [newProviderOpen, setNewProviderOpen] = useState(false);

  const handleProviderChange = (value: string) => {
    if (value === NEW_PROVIDER_OPTION) {
      setNewProviderOpen(true);
      return;
    }
    setProviderId(value);
  };

  const handleAdd = async () => {
    setError(null);
    if (!domain.trim() || !providerId) {
      setError("Domain and provider are required");
      return;
    }

    try {
      await post("/api/global-auth/sso-domains", {
        domain: domain.trim().toLowerCase(),
        providerId,
      });
      setDomain("");
      setProviderId("");
      revalidateDomains();
      revalidateAvailable();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleRemove = async (d: string) => {
    if (!confirm(`Remove SSO configuration for ${d}?`)) return;
    try {
      await del(`/api/global-auth/sso-domains/${encodeURIComponent(d)}`);
      revalidateDomains();
      revalidateAvailable();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const findProvider = (id: string) => providers?.find((p) => p.id === id);

  return (
    <>
      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Restricted require="privileged">
        <Row form className="align-items-end">
          <FormGroup className="col-md-5">
            <Label for="new-domain">Domain</Label>
            <Input
              id="new-domain"
              type="text"
              placeholder="example.com"
              value={domain}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setDomain(e.target.value)
              }
            />
          </FormGroup>
          <FormGroup className="col-md-5">
            <Label for="new-domain-provider">Provider</Label>
            <Input
              id="new-domain-provider"
              type="select"
              value={providerId}
              onChange={(e) => handleProviderChange(e.target.value)}
            >
              <option value="">Select provider</option>
              {availableProviders?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              <option value={NEW_PROVIDER_OPTION}>+ New provider…</option>
            </Input>
          </FormGroup>
          <FormGroup className="col-md-2">
            <Button block primary onClick={handleAdd}>
              Add Domain
            </Button>
          </FormGroup>
        </Row>
      </Restricted>

      {domains && domains.length === 0 && (
        <p className="text-muted">No domains configured.</p>
      )}
      {domains?.map((d) => {
        const provider = findProvider(d.providerId);
        return (
          <div
            key={d.id}
            className="border px-3 d-flex align-items-center mb-2"
          >
            <span style={{ flex: "0 0 41.6667%" }}>{d.domain}</span>
            <span style={{ flex: "0 0 41.6667%" }} className="text-muted pl-3">
              <Link href={`/global-auth/sso/providers/${d.providerId}`}>
                <a>{provider?.name ?? d.providerId}</a>
              </Link>
              {provider && !provider.enabled && (
                <Badge color="secondary" className="ml-2">
                  Disabled
                </Badge>
              )}
            </span>
            <Restricted require="privileged">
              <span className="ml-auto">
                <IconButton
                  id={`remove-${d.domain.replace(/[^a-zA-Z0-9-]/g, "-")}`}
                  type="remove"
                  onClick={() => handleRemove(d.domain)}
                />
              </span>
            </Restricted>
          </div>
        );
      })}

      <FormModal
        title="New SSO Provider"
        size="lg"
        open={newProviderOpen}
        onClose={() => setNewProviderOpen(false)}
        new
        path="/api/global-auth/sso-providers"
        validationSchema={newProviderSchema}
        initValues={initialProvider}
        onSave={(provider?: Provider) => {
          revalidateAvailable();
          revalidateProviders();
          setNewProviderOpen(false);
          if (provider?.id) setProviderId(provider.id);
        }}
      >
        <ProviderFormFields isNew />
      </FormModal>
    </>
  );
}
